import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { content } from "../../data/content";

const { year, month, day } = content.friendsSince;
const START = new Date(year, month - 1, day, 0, 0, 0);

function getDiff(now) {
  let years = now.getFullYear() - START.getFullYear();
  let anniv = new Date(now.getFullYear(), START.getMonth(), START.getDate());
  if (now < anniv) {
    years -= 1;
    anniv = new Date(now.getFullYear() - 1, START.getMonth(), START.getDate());
  }
  let ms = now - anniv;
  const days = Math.floor(ms / 86400000);
  ms -= days * 86400000;
  const hours = Math.floor(ms / 3600000);
  ms -= hours * 3600000;
  const mins = Math.floor(ms / 60000);
  ms -= mins * 60000;
  const secs = Math.floor(ms / 1000);
  return { years, days, hours, mins, secs };
}

const pad = (n) => String(n).padStart(2, "0");

function Unit({ value, label, color }) {
  return (
    <div className="flex flex-col items-center">
      <div
        className={`${color} min-w-[62px] md:min-w-[96px] rounded-2xl border-[3px] border-ink shadow-brutal px-2 py-3 md:px-4 md:py-5`}
      >
        <span className="font-heading font-black text-3xl md:text-6xl text-ink tabular-nums">
          {value}
        </span>
      </div>
      <span className="mt-2 font-body font-bold text-[10px] md:text-sm uppercase tracking-widest text-white/70">
        {label}
      </span>
    </div>
  );
}

export function Intro({ onDone }) {
  const [d, setD] = useState(() => getDiff(new Date()));

  useEffect(() => {
    const id = setInterval(() => setD(getDiff(new Date())), 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <motion.div
      data-testid="intro-overlay"
      initial={{ y: 0 }}
      exit={{ y: "-100%" }}
      transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
      className="fixed inset-0 z-[10000] bg-ink text-white flex flex-col items-center justify-center px-5 overflow-hidden"
    >
      <motion.p
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="font-accent text-2xl md:text-4xl text-sun mb-6 text-center -rotate-2"
      >
        {content.introKicker}
      </motion.p>

      <motion.h1
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35 }}
        className="font-heading font-black uppercase tracking-tighter text-center text-2xl md:text-5xl leading-tight max-w-3xl"
      >
        {content.introLine}
      </motion.h1>

      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.5 }}
        className="mt-10 flex items-end gap-2 md:gap-4"
        data-testid="intro-counter"
      >
        <Unit value={d.years} label="años" color="bg-rose" />
        <Unit value={pad(d.days)} label="días" color="bg-grape" />
        <Unit value={pad(d.hours)} label="horas" color="bg-sky" />
        <Unit value={pad(d.mins)} label="min" color="bg-butter" />
        <Unit value={pad(d.secs)} label="seg" color="bg-sun" />
      </motion.div>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.7 }}
        className="mt-8 font-body font-semibold text-white/70 text-center text-sm md:text-lg max-w-md"
      >
        {content.introSubline}
      </motion.p>

      <motion.button
        data-testid="intro-continue-btn"
        onClick={onDone}
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="mt-12 inline-flex items-center gap-2 rounded-full bg-white text-ink font-body font-extrabold px-8 py-4 text-base md:text-lg border-[3px] border-ink shadow-brutal"
      >
        {content.introButton}
        <ArrowDown className="w-5 h-5" />
      </motion.button>
    </motion.div>
  );
}
